import React, { useState } from 'react';
import ModalCloseButton from './ui/ModalCloseButton';
import { trackEvent } from '../utils/telemetry';

export default function DigitalInviteModal({ isOpen, onClose, theme = {}, eventData = {} }) {
  const [copied, setCopied] = useState(false);
  const [tone, setTone] = useState('fun');

  const themeName = theme?.name || eventData?.theme || 'Extraordinary Party';
  const occasion = eventData?.occasion || 'Celebration';
  const dressCode = theme?.dress_code?.recommendation || theme?.dress_code || 'Smart Casual Chic';
  const palette = theme?.palette || [
    { hex: '#F43F5E', name: 'Rose' },
    { hex: '#FB923C', name: 'Amber' },
    { hex: '#FBBF24', name: 'Warm Gold' },
    { hex: '#0F172A', name: 'Midnight' }
  ];

  const [inviteData, setInviteData] = useState({
    title: `${occasion}: ${themeName}`,
    date: eventData?.date || 'Saturday, 7:00 PM',
    location: eventData?.venue_name || eventData?.zip_code ? `Venue (Near ${eventData.zip_code})` : 'Private Venue',
    dressCode: dressCode,
    customNote: 'Drinks, bites, and great music provided! Please RSVP so we can finalize numbers.'
  });

  if (!isOpen) return null;

  const getToneCopy = () => {
    switch (tone) {
      case 'chic':
        return `You are cordially invited to celebrate ${occasion}.\nTheme: ${themeName}\n\nWhen: ${inviteData.date}\nWhere: ${inviteData.location}\nAttire: ${inviteData.dressCode}\n\n${inviteData.customNote}\n\nRSVP by clicking the link below:`;
      case 'family':
        return `🎈 We're celebrating! Join us for ${occasion} with our ${themeName} theme!\n\n📅 When: ${inviteData.date}\n📍 Where: ${inviteData.location}\n👕 What to wear: ${inviteData.dressCode}\n\n${inviteData.customNote}\n\nPlease RSVP below!`;
      case 'vip':
        return `✨ You're on the guest list: ${inviteData.title}\n\nExperience: ${themeName} aesthetic with custom cocktails and curated sounds.\n\nDate: ${inviteData.date}\nLocation: ${inviteData.location}\nDress code: ${inviteData.dressCode}\n\n${inviteData.customNote}\n\nConfirm attendance below:`;
      case 'fun':
      default:
        return `🎉 You're invited to celebrate ${occasion}!\nTheme: ${themeName}\n\n🕒 When: ${inviteData.date}\n📍 Where: ${inviteData.location}\n👗 Dress Vibe: ${inviteData.dressCode}\n\n${inviteData.customNote}\n\nRSVP here:`;
    }
  };

  const inviteText = getToneCopy();

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteText).then(() => {
      setCopied(true);
      trackEvent('invite_text_copied', { tone, theme: themeName });
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {});
  };

  // Google Calendar URL generator
  const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(inviteData.title)}&details=${encodeURIComponent(inviteText)}&location=${encodeURIComponent(inviteData.location)}`;

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div 
        className="relative w-full max-w-2xl max-h-[92vh] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden text-left"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-100 bg-gradient-to-r from-stone-50 via-white to-stone-50 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-pink-100 text-pink-600 text-xl">
              💌
            </span>
            <div>
              <h2 className="text-base sm:text-lg font-black text-stone-900 leading-tight">
                Digital Invites & RSVP Hub
              </h2>
              <p className="text-xs text-stone-500">
                Crafted for <span className="font-semibold text-stone-800">{themeName}</span>
              </p>
            </div>
          </div>
          <ModalCloseButton onClose={onClose} />
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
          {/* Tone Selector */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">
              Invite Tone & Vibe
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { id: 'fun', label: '🎉 Casual & Fun' },
                { id: 'chic', label: '✨ Chic & Curated' },
                { id: 'family', label: '🎈 Family Friendly' },
                { id: 'vip', label: '🍸 VIP & Sleek' },
              ].map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTone(t.id)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold transition text-center ${
                    tone === t.id
                      ? 'bg-stone-900 text-white shadow-sm'
                      : 'bg-stone-50 hover:bg-stone-100 text-stone-700 border border-stone-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Live Preview Card */}
          <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-stone-900 via-stone-850 to-stone-950 text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-primary/20 rounded-full blur-2xl pointer-events-none" />
            <div className="relative z-10 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-widest text-primary-light">
                  {occasion} Invitation
                </span>
                {/* Palette Dots */}
                <div className="flex items-center gap-1.5">
                  {palette.slice(0, 5).map((c, idx) => {
                    const hex = typeof c === 'object' ? c.hex : c;
                    return (
                      <span
                        key={idx}
                        className="w-3.5 h-3.5 rounded-full border border-white/20 shadow-sm"
                        style={{ backgroundColor: hex }}
                        title={typeof c === 'object' ? c.name : hex}
                      />
                    );
                  })}
                </div>
              </div>

              <h3 className="text-xl sm:text-2xl font-black text-white leading-tight">
                {inviteData.title}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-stone-300 pt-2 border-t border-white/10">
                <div>
                  <span className="block text-[10px] text-stone-400 font-bold uppercase">When</span>
                  <span className="font-semibold text-white">{inviteData.date}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-stone-400 font-bold uppercase">Where</span>
                  <span className="font-semibold text-white truncate block">{inviteData.location}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-stone-400 font-bold uppercase">Dress Vibe</span>
                  <span className="font-semibold text-white">{inviteData.dressCode}</span>
                </div>
              </div>

              <p className="text-xs text-stone-300 pt-1 leading-relaxed italic">
                "{inviteData.customNote}"
              </p>
            </div>
          </div>

          {/* Action Row: Copy & RSVP Launchers */}
          <div className="space-y-2.5">
            <p className="text-xs font-bold uppercase tracking-wider text-stone-500">
              Export & Collect RSVPs
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={handleCopy}
                className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl font-bold text-xs transition shadow-sm active:scale-[0.98] ${
                  copied
                    ? 'bg-emerald-600 text-white'
                    : 'bg-primary hover:bg-primary-dark text-white'
                }`}
              >
                <span>{copied ? '✓' : '📋'}</span>
                <span>{copied ? 'Copied to Clipboard!' : 'Copy Invite Text'}</span>
              </button>

              <a
                href="https://partiful.com/create"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('rsvp_launcher_clicked', { platform: 'Partiful' })}
                className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs transition shadow-sm active:scale-[0.98]"
              >
                <span>✨</span>
                <span>Host on Partiful</span>
                <span className="text-[10px]">↗</span>
              </a>

              <a
                href="https://www.paperlesspost.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('rsvp_launcher_clicked', { platform: 'Paperless Post' })}
                className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-stone-900 hover:bg-stone-800 text-white font-bold text-xs transition shadow-sm active:scale-[0.98]"
              >
                <span>✉️</span>
                <span>Paperless Post</span>
                <span className="text-[10px]">↗</span>
              </a>
            </div>

            <div className="flex items-center justify-end pt-1">
              <a
                href={gcalUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('gcal_export_clicked')}
                className="text-xs font-bold text-stone-600 hover:text-stone-900 flex items-center gap-1"
              >
                <span>📅</span>
                <span>Generate Google Calendar Link ↗</span>
              </a>
            </div>
          </div>

          {/* Editable Textarea */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">
              Full Copy Preview (Ready for WhatsApp / iMessage / DM)
            </label>
            <textarea
              rows={5}
              readOnly
              value={inviteText}
              className="w-full p-3 rounded-xl border border-stone-200 bg-stone-50 font-mono text-xs text-stone-800 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Free iteration note */}
          <div className="rounded-xl bg-emerald-50 border border-emerald-200/80 p-3 flex items-center gap-2.5 text-xs text-emerald-900 font-medium">
            <span className="text-base">🎉</span>
            <span>Digital invites, checklist exports, and plan customizations are always <strong>100% free and unlimited</strong>.</span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-stone-100 bg-stone-50 flex items-center justify-between">
          <span className="text-xs text-stone-500">Copy text or link straight to your favorite RSVP tool.</span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-stone-200 hover:bg-stone-300 text-stone-800 font-bold text-xs transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
